import _ from 'lodash';
import type { z } from 'zod';
import { getClassLabel } from '../classes/codecs';
import { Subject } from '../common/enums';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { encodeRecordId } from '../recordIds';
import {
  getTutorialStartDatetimeIdentifier,
  parseTutorialStartDatetimeIdentifier,
} from '../tutorials/codecs';
import { StudentAttributes } from './constants';
import { DBStudentSchema, StudentSchema } from './schemas';

type Student = z.infer<typeof StudentSchema>;
type DBStudent = z.infer<typeof DBStudentSchema>;

export const StudentCodecConfig: TransformerConfig<typeof StudentSchema, typeof DBStudentSchema> = {
  name: 'Student',
  clientSchema: StudentSchema,
  dbSchema: DBStudentSchema,
  transformers: [
    createRenameTransformer<typeof StudentSchema, typeof DBStudentSchema>('id', 'studentId'),
    createRenameTransformer<typeof StudentSchema, typeof DBStudentSchema>('slackUserId', 'slackId'),
    createRenameTransformer<typeof StudentSchema, typeof DBStudentSchema>(
      'slackChannelIds',
      'slackChannels'
    ),
    createRenameTransformer<typeof StudentSchema, typeof DBStudentSchema>(
      'xeroContactId',
      'xeroId'
    ),

    // classes: [{ classId, subject, classCode, classLabel, courseId, streamId, streamIds }] ↔
    //   classes: { "subject#classCode": { course, stream, streams } }
    // An entry stored without the class's stream map reads as holding the stream in use
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['classes'] as const,
        encode: ({ classes }) => ({
          classes: Object.fromEntries(
            classes.map((val: Student['classes'][number]) => [
              `${val.subject}#${val.classCode}`,
              {
                course: val.courseId,
                stream: val.streamId,
                ...(val.streamIds !== undefined ? { streams: val.streamIds } : {}),
              },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['classes'] as const,
        decode: ({ classes }) => ({
          classes: Object.entries(classes ?? {}).map(([compositeClassId, val]) => {
            const [subject, classCode] = compositeClassId.split('#') as [Subject, string];
            const entry = val as NonNullable<DBStudent['classes']>[string];
            return {
              classId: encodeRecordId('class', { subject, code: classCode }),
              subject,
              classCode,
              classLabel: getClassLabel({ courseId: entry.course, code: classCode }),
              courseId: entry.course,
              streamId: entry.stream,
              streamIds: entry.streams ?? { [entry.course]: entry.stream },
            };
          }),
        }),
      },
    }),

    // courseStates: [{ id, studentId, subject, courseId, streamId?, ... }] ↔
    //   courseStates: { "subject#course": { ... } } OR { "subject#course#stream": { ... } }
    // The DB key is the source of truth for identity: stream lives only in the key,
    // never in the value. On decode we reconstruct subject/courseId/streamId from the key.
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['courseStates'] as const,
        encode: ({ courseStates }) => {
          const entries = courseStates.map((val: Student['courseStates'][number]) => {
            const { id: _id, studentId: _studentId, courseId, streamId, ...rest } = val;
            return [
              streamId ? `${val.subject}#${courseId}#${streamId}` : `${val.subject}#${courseId}`,
              { ...rest, course: courseId },
            ] as const;
          });

          // Object.fromEntries keeps only the LAST entry for a colliding key — silently
          // dropping a course state's credits. Duplicate identities always mean corrupted
          // in-memory state, so fail loudly instead of losing money-critical data.
          const seenKeys = new Set<string>();
          for (const [key] of entries) {
            if (seenKeys.has(key)) {
              throw new Error(`Duplicate course state identity: ${key}`);
            }
            seenKeys.add(key);
          }

          return { courseStates: Object.fromEntries(entries) };
        },
      },
      decoder: {
        sourceFields: ['courseStates', 'studentId'] as const,
        // Each course state's id needs the student's, so a read without it gives no course states
        decode: ({ courseStates, studentId }) =>
          courseStates === undefined || studentId === undefined
            ? {}
            : {
                courseStates: Object.entries(courseStates).map(([compositeKey, val]) => {
                  const [subject, courseId, streamId] = compositeKey.split('#') as [
                    Subject,
                    string,
                    string | undefined,
                  ];
                  const { course: _course, ...rest } = val as DBStudent['courseStates'][string];
                  const parts = {
                    studentId,
                    subject,
                    courseId,
                    ...(streamId !== undefined ? { streamId } : {}),
                  };
                  return { ...rest, id: encodeRecordId('courseState', parts), ...parts };
                }),
              },
      },
    }),

    // tutorials: [{ tutorialId, courseId, startDatetime, suffix, duration }] ↔
    // tutorials: { startDatetimeIdentifier: { duration, course, startDatetimeIdentifier } }
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['tutorials'] as const,
        encode: ({ tutorials }) => ({
          tutorials: Object.fromEntries(
            tutorials.map((tutorial: Student['tutorials'][number]) => {
              const startDatetimeIdentifier = getTutorialStartDatetimeIdentifier(tutorial);
              return [
                startDatetimeIdentifier,
                { duration: tutorial.duration, course: tutorial.courseId, startDatetimeIdentifier },
              ];
            })
          ),
        }),
      },
      decoder: {
        sourceFields: ['tutorials'] as const,
        decode: ({ tutorials }) => ({
          tutorials: Object.entries(tutorials ?? {}).map(([startDatetimeIdentifier, val]) => {
            const { duration, course } = val as NonNullable<DBStudent['tutorials']>[string];
            const parts = {
              courseId: course,
              ...parseTutorialStartDatetimeIdentifier(startDatetimeIdentifier),
            };
            return { tutorialId: encodeRecordId('tutorial', parts), ...parts, duration };
          }),
        }),
      },
    }),

    // waitlistedClasses: [{ classId, subject, classCode, addedDatetime }] ↔
    //   waitlistedClasses: [{ classId: the class's code, subject, addedDatetime }]
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: ['waitlistedClasses'] as const,
        encode: ({ waitlistedClasses }) => ({
          waitlistedClasses: (waitlistedClasses as Student['waitlistedClasses']).map(
            ({ subject, classCode, addedDatetime }) => ({
              addedDatetime,
              classId: classCode,
              subject,
            })
          ),
        }),
      },
      decoder: {
        sourceFields: ['waitlistedClasses'] as const,
        decode: ({ waitlistedClasses }) =>
          waitlistedClasses === undefined
            ? {}
            : {
                waitlistedClasses: waitlistedClasses.map(({ classId, subject, addedDatetime }) => ({
                  classId: encodeRecordId('class', { subject, code: classId }),
                  subject,
                  classCode: classId,
                  addedDatetime,
                })),
              },
      },
    }),

    // Handle search string updates
    createTransformer<typeof StudentSchema, typeof DBStudentSchema>({
      encoder: {
        sourceFields: StudentAttributes.Search,
        // No dependencies declared: the parent* search fields are optional and
        // may be absent on a student. Recompute searchStr by falling back to the
        // existing student (then '') so a partial update touching one search field
        // doesn't require every other search field to be present.
        encode: (student, oldStudent) => {
          let searchStr = '';
          StudentAttributes.Search.forEach(attr => {
            const val = student?.[attr] ?? oldStudent?.[attr] ?? '';
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(student, StudentAttributes.Search) };
        },
      },
    }),
  ],
};

export const encodeStudentToDb = createEncodeToDbFn(StudentCodecConfig);
export const decodeStudentFromDb = createDecodeFromDbFn(StudentCodecConfig);
